import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { DocumentService, LoaderService } from '@app/shared/services';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AskPasswordProtectedComponent } from '@app/modules/inbox-v2/component/my-inbox/my-inbox-table/ask-password-protected/ask-password-protected.component';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from '@env/environment';
import { AnimationOptions } from 'ngx-lottie';
import { SharedDocViewerDialogComponent } from '@app/shared/component/shared-doc-viewer-dialog/shared-doc-viewer-dialog.component';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'form-attachment',
  templateUrl: './form-attachment.component.html',
  styleUrl: './form-attachment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormAttachmentComponent implements OnInit, OnDestroy {
  @ViewChild('picker') datePicker: MatDatepicker<Date>;
  @ViewChild('dateInput') dateInput: any;
  @Input() files: any[] = [];
  @Input() currentFormId: string;
  @Input() currentuserId: string;
  @Input() currentTemplate: any;
  @Input() isRemoveEditSaveSharing = false;
  @Output() locationTagEventEmitter = new EventEmitter<any>();
  @Output() reloadFileEventEmitter = new EventEmitter<any>();

  summaryData = '';
  is_password_protected = false;
  loadingItems = [{ id: 1 }, { id: 2 }, { id: 3 }];
  is_loading = false;
  is_removing_password = false;
  currentFile;
  enableTab = 'Preview';
  subscription = new Subscription();
  isHovering = false;
  timeoutId;
  animation: AnimationOptions = {
    path: '/assets/lottie/animation-4.json',
  };
  isShowExploreWithAiChat = false;
  isUpdatingSummary = false;

  // Worker references for proper cleanup
  private inboxSuggestionWorker: Worker | undefined;
  private manualSummaryWorker: Worker | undefined;

  constructor(
    private documentService: DocumentService,
    private loaderService: LoaderService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private _sharedVaultService: SharedVaultService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.files.length && this.files[0].is_password_protected) {
      this.is_password_protected = true;
    } else {
      this.is_password_protected = false;
    }
    this.cd.detectChanges();
  }

  getUrl(id, index, fileName, filsrData) {
    this.dialog.open(SharedDocViewerDialogComponent, {
      width: '100%',
      height: '100%',
      panelClass: 'shared-doc-view-dialog',
      data: {
        index: index,
        file: { ...this.files[0], file_name: this.files[0].name },
        urlLink: filsrData,
      },
    });
  }

  openPasswordProtectedDialog() {
    const modalRef = this.dialog.open(AskPasswordProtectedComponent, {
      panelClass: 'full-window-dialog-2',
      disableClose: true,
      autoFocus: false,
      data: {
        file: {
          ...this.files[0],
          id: this.files[0].vault_user_form_uploads_id,
        },
      },
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe((res) => {
        if (res.success) {
          this.is_removing_password = true;
          this.cd.detectChanges();
          this.getInboxSuggestion(res.res.data_extraction_execution_id);
        }
      })
    );
  }
  async getInboxSuggestion(data_extraction_id) {
    // Terminate previous worker if exists
    if (this.inboxSuggestionWorker) {
      this.inboxSuggestionWorker.terminate();
    }

    return await new Promise(async (resolve) => {
      if (typeof Worker !== 'undefined') {
        this.inboxSuggestionWorker = new Worker(new URL('./my-inbox-suggestion-by-id.worker', import.meta.url), { type: 'module' });

        let data = {
          token: '',
          url: '',
        };
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        const token = `Bearer ${idToken}`;
        data.token = token;

        data.url = `${environment.apiUrl}/api/data-extraction/data-extraction-suggestion-by-id/${data_extraction_id}`;
        this.inboxSuggestionWorker.onmessage = async ({ data }) => {
          //return security key generated by worker process
          resolve(data);
          let res = data.data.data[0];

          this.cd.detectChanges();
          this.timeoutId = setTimeout(() => {
            if (res.status !== 'end') {
              this.getInboxSuggestion(data_extraction_id); // Call the method again after 5 seconds
            } else {
              // this.is_removing_password = false;
              this.reloadFileEventEmitter.emit();
              this.loaderService.hide();
            }
          }, 15000);

          this.cd.detectChanges();
        };
        this.inboxSuggestionWorker.postMessage(data);
      } else {
        //browser does not support web-workers so just return a new data key, UI automation will be blocked
        resolve(null);
      }
    });
  }
  getAiData() {
    if (this.is_loading && this.enableTab !== 'Summary') return;
    this.is_loading = true;
    this.subscription.add(
      this.documentService.getFormSummaryData(this.currentFormId).subscribe((res) => {
        this.summaryData = res?.data?.summary_data;
        if (this.enableTab === 'Explore with Ai') {
          this.isShowExploreWithAiChat = true;
        }
        if (this.enableTab === 'Summary') {
          this.isUpdatingSummary = false;
          this.is_loading = false;
        }
        this.cd.detectChanges();
      })
    );
  }

  updateFileuploadedDate(data) {
    const payload = {
      id: data['delete_id'],
      document_date: data.year,
      vault_user_form_id: this.currentFormId,
    };
    this.subscription.add(
      this.documentService.setFileuploadedStatus(payload).subscribe(
        (res) => {
          this.loaderService.show();
          this.reloadFileEventEmitter.emit({ donotRefreshDocument: true });
          this.cd.detectChanges();
        },
        (error) => {
          this.toastr.error('Error uploading File.', error?.error?.message);
        }
      )
    );
    this.cd.detectChanges();
  }

  openDatePicker(file: any) {
    this.currentFile = file;
    if (file.year) {
      const existingDate = new Date(parseInt(file.year), 0, 1);
      this.dateInput.nativeElement.value = existingDate;
    }
    this.datePicker.open();
  }

  onDateChange(event: any) {
    if (event.value && this.currentFile) {
      this.currentFile.year = event.value.format('YYYY-MM-DD');
      this.updateFileuploadedDate(this.currentFile);
    }
  }

  manageLocation(file) {
    this.locationTagEventEmitter.emit(file);
  }

  updateSummaryData() {
    this.subscription.add(
      this._sharedVaultService
        .refreshDocument({
          vault_user_form_id: this.currentFormId,
          user_id: this.currentuserId,
          execution_type: 'document_template_manual_summary',
        })
        .subscribe((res) => {
          this.summaryData = '';
          this.isUpdatingSummary = true;
          this.checkStatus();
        })
    );
  }
  updateStatusWorking() {
    this.summaryData = '';
    this.isUpdatingSummary = true;
    this.cd.detectChanges();
  }
  async checkStatus() {
    this.summaryData = '';
    this.isUpdatingSummary = true;
    this.cd.detectChanges();
    return await new Promise(async (resolve) => {
      if (typeof Worker !== 'undefined') {
        const worker = new Worker(new URL('./form-attachment-manual-summary-update.worker', import.meta.url), { type: 'module' });

        const data = {
          token: '',
          url: `${environment.apiUrl}/api/vault/check-upload-file-ai-running`,
          vault_user_form_id: this.currentFormId,
          execution_type: 'document_template_manual_summary',
        };
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        const token = `Bearer ${idToken}`;
        data.token = token;
        this.manualSummaryWorker.onmessage = async ({ data }) => {
          if (data.data.ai_is_running) {
            setTimeout(() => {
              this.checkStatus();
            }, 3000);
          } else {
            this.is_loading = false;
            this.getAiData();
          }
          resolve(data);
        };
        this.manualSummaryWorker.postMessage(data);
      } else {
        resolve(null);
      }
    });
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Terminate workers
    if (this.inboxSuggestionWorker) {
      this.inboxSuggestionWorker.terminate();
      this.inboxSuggestionWorker = undefined;
    }
    if (this.manualSummaryWorker) {
      this.manualSummaryWorker.terminate();
      this.manualSummaryWorker = undefined;
    }
  }
}
